'use client';

import { useEffect, useRef } from 'react';
import type { Locale } from '../HomeLanding';

const baseBenefits = [
  {
    title: 'Optimized FCR & Energy Density',
    body: 'With a caloric value of approx. 9,000 kcal/kg, it provides the essential energy needed for rapid growth and improved Feed Conversion Ratios.',
  },
  {
    title: 'Natural Omega-3 for Vitality',
    body: 'Supports the immune system and reduces inflammation in high-density farming environments, particularly in shrimp and carnivorous fish species.',
  },
  {
    title: 'Bioavailability & Palatability',
    body: 'Natural marine attractants increase feed intake and help animals reach target weights faster.',
  },
  {
    title: 'Superior Skin & Scale Integrity',
    body: 'Promotes healthy skin, glossy coats, and robust scale development as indicators of health and market value.',
  },
];

export default function IndustrialFeedBenefits({ locale = 'en' }: { locale?: Locale }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isIndonesian = locale === 'id';
  const isVietnamese = locale === 'vn';
  const isChinese = locale === 'cn';

  const benefits =
    locale === 'vn'
      ? [
          {
            title: 'Tối ưu FCR & mật độ năng lượng',
            body: 'Với giá trị calo khoảng 9.000 kcal/kg, sản phẩm cung cấp năng lượng cần thiết để tăng trưởng nhanh và cải thiện hệ số chuyển đổi thức ăn.',
          },
          {
            title: 'Omega-3 tự nhiên cho sức khỏe',
            body: 'Hỗ trợ hệ miễn dịch và giảm viêm trong môi trường nuôi mật độ cao, đặc biệt ở tôm và các loài cá ăn thịt.',
          },
          {
            title: 'Khả dụng sinh học & độ ngon miệng',
            body: 'Các chất dẫn dụ biển tự nhiên làm tăng khả năng ăn vào và giúp vật nuôi đạt trọng lượng mục tiêu nhanh hơn.',
          },
          {
            title: 'Tốt cho da và vảy',
            body: 'Hỗ trợ da khỏe mạnh, lông bóng và vảy phát triển vững chắc như chỉ báo cho sức khỏe và giá trị thương phẩm.',
          },
        ]
      : locale === 'id'
        ? [
            {
              title: 'Optimasi FCR & kepadatan energi',
              body: 'Dengan nilai kalori sekitar 9.000 kcal/kg, produk ini memberikan energi penting untuk pertumbuhan lebih cepat dan optimalisasi nilai FCR (Feed Conversion Ratio).',
            },
            {
              title: 'Omega-3 alami untuk vitalitas',
              body: 'Meningkatkan sistem imun dan menekan peradangan pada lingkungan budidaya padat tebar, khususnya untuk udang dan ikan karnivora.',
            },
            {
              title: 'Bioavailabilitas & palatabilitas',
              body: 'Aroma laut alami meningkatkan nafsu makan (palatabilitas) sehingga ternak dan komoditas akuakultur mencapai bobot target lebih cepat.',
            },
            {
              title: 'Kesehatan Kulit & Kualitas Sisik Lebih Optimal',
              body: 'Mendukung kesehatan kulit, tampilan mengkilap, serta perkembangan sisik yang kuat sebagai indikator kesehatan dan standar nilai jual tinggi.',
            },
        ]
      : locale === 'cn'
          ? [
              {
                title: '优化 FCR 与能量密度',
                body: '约 9,000 kcal/kg 的热量值，为快速生长和更好的料肉比提供关键能量。',
              },
              {
                title: '天然 Omega-3，提升活力',
                body: '支持免疫系统，并减少高密度养殖环境中的炎症，特别适用于虾类和肉食性鱼类。',
              },
              {
                title: '更高的生物利用率与适口性',
                body: '天然海洋诱食因子可提高采食量，帮助动物更快达到目标体重。',
              },
              {
                title: '更佳的皮肤与鳞片完整性',
                body: '促进健康皮肤、光泽外观和坚韧鳞片发育，体现健康与市场价值。',
              },
            ]
          : baseBenefits;

  useEffect(() => {
    if (!sectionRef.current) return;
    const items = sectionRef.current.querySelectorAll('.bf-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Array.from(items).indexOf(entry.target as Element);
            setTimeout(() => entry.target.classList.add('active'), index * 80);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="sustainability" ref={sectionRef} className="py-24 bg-surface-alt scroll-mt-20">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="bf-reveal reveal mb-8 flex items-center gap-3">
          <span className="inline-block w-6 h-px bg-blue" />
          <span className="font-mono text-[11px] tracking-[0.18em] text-blue uppercase font-medium">
            {isVietnamese
              ? 'Lợi ích thức ăn công nghiệp'
              : isIndonesian
                ? 'Manfaat pakan industri'
              : isChinese
                  ? '工业饲料收益'
                  : 'Industrial Feed Benefits'}
          </span>
        </div>

        <div className="bf-reveal reveal reveal-delay-1 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <h2
              className="font-bold tracking-[-0.04em] text-charcoal leading-tight"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              {isVietnamese
                ? 'Thiết kế cho hiệu suất thức ăn.'
                : isIndonesian
                  ? 'Optimasi Performa Pakan.'
                : isChinese
                    ? '面向饲料性能。'
                    : 'Built for feed performance.'}
              <br />
              <span className="text-titanium font-light">
                {isVietnamese
                  ? 'Thiết kế cho kết quả dinh dưỡng.'
                  : isIndonesian
                    ? 'Formulasi Tepat untuk Nutrisi Maksimal.'
                  : isChinese
                      ? '为最大化营养而精准配方。'
                      : 'Designed for nutrition outcomes.'}
              </span>
            </h2>
          </div>
          <p className="text-titanium text-[15px] leading-relaxed max-w-xl">
            {isVietnamese
              ? 'Dầu cá thô PT. WWI hỗ trợ hiệu quả phối trộn trong nuôi trồng thủy sản và chăn nuôi.'
              : isIndonesian
                ? 'Crude Fish Oil PT. WWI mendukung efisiensi formulasi pakan untuk sektor akuakultur dan peternakan. Cocok untuk kebutuhan Bahan Baku Pakan Ikan maupun supplier minyak ikan yang mengutamakan konsistensi.'
                : isChinese
                  ? 'PT. WWI 粗鱼油可提升水产与畜牧饲料的配方表现。'
                  : 'PT. WWI crude fish oil supports formulation performance across aquaculture and livestock diets.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {benefits.map((item) => (
            <div key={item.title} className="bf-reveal reveal bg-surface border border-divider rounded-2xl p-6 shadow-card">
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-blue mb-3">
                {isVietnamese
                  ? 'Lợi ích thức ăn'
                  : isIndonesian
                    ? 'Manfaat pakan'
                    : isChinese
                    ? '饲料收益'
                    : 'Feed Benefit'}
              </div>
              <h3 className="text-charcoal font-semibold text-xl mb-3 tracking-tight">{item.title}</h3>
              <p className="text-titanium text-[14px] leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


